import React from 'react';
import {TripResponse} from '../../store/backendApi';
import {CheckCircleIcon, GlobeAltIcon, LinkIcon, LockClosedIcon,} from '@heroicons/react/24/outline';
import {Button} from '../../components/Button';
import {useSharingOptions} from '../../hooks/editTrip/useSharingOptions';

export interface SharingOptionsPageProps {
  trip: TripResponse;
}

interface ShareOptionProps {
  id: string;
  icon: React.ReactNode;
  title: string;
  description: string;
  isSelected: boolean;
  onSelect: () => void;
  children?: React.ReactNode;
}

const ShareOption = ({
  icon,
  title,
  description,
  isSelected,
  onSelect,
  children,
}: ShareOptionProps) => {
  return (
    <div className="space-y-3">
      <div
        onClick={onSelect}
        className={`relative rounded-xl p-4 cursor-pointer transition-all duration-200 border-2
          ${
            isSelected
              ? 'bg-secondary border-brand shadow-md'
              : 'bg-surface border-default hover:border-brand shadow-sm hover:shadow-md'
          }`}
      >
        <div className="flex items-start gap-3">
          <div
            className={`flex-shrink-0 p-2 rounded-lg transition-colors ${
              isSelected ? 'bg-subtle text-brand' : 'bg-muted text-muted'
            }`}
          >
            {icon}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between">
              <h4 className="text-sm  text-main">{title}</h4>
              {isSelected && <CheckCircleIcon className="w-5 h-5 text-brand flex-shrink-0" />}
            </div>
            <p className="text-xs text-muted mt-1">{description}</p>
          </div>
        </div>
      </div>
      {isSelected && children && (
        <div className="ml-4 pl-4 border-l-2 border-brand space-y-3 animate-in slide-in-from-top-2 duration-200">
          {children}
        </div>
      )}
    </div>
  );
};

export const SharingOptionsPage = (props: SharingOptionsPageProps) => {
  // All business logic is now in the hook
  const {
    shareMode,
    slug,
    setShareMode,
    setSlug,
    publicUrl,
    privateUrlBase,
    hasChanges,
    updateSharing,
    reset,
    patchTripLoading,
  } = useSharingOptions(props.trip);

  return (
    <div className="space-y-5">
      <p className="text-sm text-muted">Control who can view your trip</p>

      {/* Share Options */}
      <div className="space-y-4">
        <ShareOption
          id="public"
          icon={<GlobeAltIcon className="w-5 h-5" />}
          title="Public"
          description="Anyone can find and view your trip"
          isSelected={shareMode === 'PUBLIC'}
          onSelect={() => setShareMode('PUBLIC')}
        >
          <div className="space-y-3">
            {publicUrl ? (
              <div className="text-xs text-muted">
                <p className=" mb-1">Your trip will be available at:</p>
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href={`https://${publicUrl}`}
                  className="inline-flex items-center gap-1 text-brand hover:text-brand
                     break-all hover:underline"
                >
                  <GlobeAltIcon className="w-3 h-3 flex-shrink-0" />
                  {publicUrl}
                </a>
              </div>
            ) : (
              <div className="text-xs text-muted">
                <p className=" mb-1">Your trip will be available at:</p>
                <div className="bg-muted rounded p-2 animate-pulse">
                  <span className="text-disabled">Loading URL...</span>
                </div>
              </div>
            )}
            <div className="space-y-2">
              <label className="text-xs  text-main">Customize URL</label>
              <input
                id={'slug'}
                name={'slug'}
                type={'text'}
                placeholder="my-awesome-trip"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                className="block w-full px-3 py-2 rounded-lg bg-surface border border-interactive
                  text-sm text-main placeholder:text-disabled
                  focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent
                  transition-all shadow-sm hover:border-interactive-hover"
              />
              <p className="text-xs text-muted">Use letters, numbers, and hyphens only</p>
            </div>
          </div>
        </ShareOption>

        <ShareOption
          id="link"
          icon={<LinkIcon className="w-5 h-5" />}
          title="Share with Link"
          description="Only people with the secret link can view"
          isSelected={shareMode === 'LINK'}
          onSelect={() => setShareMode('LINK')}
        >
          <div className="space-y-2">
            {props.trip.linkSecret ? (
              <div className="bg-surface rounded-lg p-3 border border-brand">
                <p className="text-xs  text-main mb-2">Secret Link:</p>
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href={`https://${privateUrlBase}/${props.trip.linkSecret}`}
                  className="inline-flex items-center gap-1 text-brand hover:text-brand
                     text-xs break-all hover:underline"
                >
                  <LinkIcon className="w-3 h-3 flex-shrink-0" />
                  {privateUrlBase}/{props.trip.linkSecret}
                </a>
              </div>
            ) : (
              <div className="bg-warning-subtle rounded-lg p-3 border border-warning">
                <p className="text-xs text-warning">
                  <span className="">💡 Note:</span> Your secret link will be generated
                  after you save these settings.
                </p>
              </div>
            )}
          </div>
        </ShareOption>

        <ShareOption
          id="private"
          icon={<LockClosedIcon className="w-5 h-5" />}
          title="Private"
          description="Only you can view your trip"
          isSelected={shareMode === 'PRIVATE'}
          onSelect={() => setShareMode('PRIVATE')}
        >
          <div className="bg-surface rounded-lg p-3 border border-default">
            <p className="text-xs text-muted">
              Your trip will only be visible to you when logged in. Perfect for personal journals or
              work-in-progress trips.
            </p>
          </div>
        </ShareOption>
      </div>

      {/* Action Buttons */}
      <div className="pt-3 border-t border-muted">
        <div className="flex gap-2">
          <Button
            type="button"
            variant="primary"
            className="flex-1"
            disabled={patchTripLoading || !hasChanges}
            loading={patchTripLoading}
            onClick={() => updateSharing()}
          >
            {patchTripLoading ? 'Saving...' : 'Save Changes'}
          </Button>
          <Button type="button" variant="secondary" onClick={reset} disabled={!hasChanges}>
            Reset
          </Button>
        </div>
      </div>
    </div>
  );
};
